import { gql, useMutation, useQuery } from '@apollo/client';
import {
  AccreditationEligibilityCriteriaEnumType,
  AccreditationStatusEnumType,
} from '__generated__/graphql';
import classNames from 'classnames';
import { FC, useState } from 'react';
import JointAccreditationDetailsDialogText from 'routes/Invest/Accreditation/JointAccreditationDetailsDialogText';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Alert from 'primitives/Alert';
import { Dialog, DialogPanel, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';

const INVESTMENT_ENTITIES_FOR_ACCREDITATION_POPUP_QUERY = gql(`
  query FetchInvestmentEntityForAccreditationPopup($id: ID!) {
    investmentEntity(id: $id) {
      id
      name
      status
      type
      createdAt
      updatedAt
      investorApplication {
        id
        accreditationStatus
        accreditationProofDocument {
          url
          name
        }
        accreditationEligibilityCriteria
      }
    }
  }
`);

const UPSERT_ACCREDITATION_APPLICATION_MUTATION = gql(`
  mutation UpsertAccreditationApplication(
    $investmentEntityId: ID!
    $accreditationStatus: AccreditationStatusEnumType!
    $accreditationProofDocument: Upload
    $accreditationEligibilityCriteria: [AccreditationEligibilityCriteriaEnumType!]
  ) {
    upsertAccreditationApplication(
      investmentEntityId: $investmentEntityId
      accreditationStatus: $accreditationStatus
      accreditationProofDocument: $accreditationProofDocument
      accreditationEligibilityCriteria: $accreditationEligibilityCriteria
    ) {
      id
      accreditationStatus
      accreditationProofDocument {
        url
        name
      }
      accreditationEligibilityCriteria
    }
  }
`);

const UPDATE_KYC_VERIFY_FLAG = gql(`
  mutation UpdateKycVerifyFlag($id: ID!, $verifyKyc: Boolean!) {
    updateKycVerifyFlag(id: $id, verifyKyc: $verifyKyc) {
      id
      verifyKyc
    }
  }
`);

const AccreditationConfirmationDialogButton: FC<{
  investmentEntityId: string;
  open: boolean;
  onClose: () => void;
  documentId?: string;
  setDisplayMessage?: (boolean) => void;
}> = ({ investmentEntityId, open, onClose, documentId, setDisplayMessage }) => {
  const [formData, setFormData] = useState<any>({});

  const { loading, error, data, refetch } = useQuery(
    INVESTMENT_ENTITIES_FOR_ACCREDITATION_POPUP_QUERY,
    {
      variables: {
        id: investmentEntityId,
      },
    }
  );

  const [upsertAccreditationApplication, { loading: mutationLoading, error: mutationError }] =
    useMutation(UPSERT_ACCREDITATION_APPLICATION_MUTATION);

  const [updateKycVerifyFlag] = useMutation(UPDATE_KYC_VERIFY_FLAG);

  if (loading) return <LoadingIndicator />;

  if (error || !data || !investmentEntityId)
    return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntity = data.investmentEntity;

  const hasSubmittedFormPreviously = () => {
    return (
      investmentEntity.investorApplication && investmentEntity.investorApplication.accreditationStatus !==
      AccreditationStatusEnumType.PendingInvestorResponse
    );
  };

  if (hasSubmittedFormPreviously()) {
    updateKycVerifyFlag({
      variables: {
        id: documentId,
        verifyKyc: false,
      },
    }).then(() => {
      onClose();
    });
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogPanel className="min-w-[720px]">
        <DialogTitle>Accreditation Information</DialogTitle>
        <div className="my-8">
          <Alert
            title="Help us understand your accreditation status"
            type="info"
            message="Fill out the form below to help us assist you better. Depending on your answers, we may need to ask you for additional information."
            buttonText="Why Get Accredited? ↗"
            onClick={() =>
              window.open('https://app.angellistindia.com/invest/accreditation', '_blank')
            }
          />
        </div>

        <div className="flex space-x-2 py-2 mt-4 mb-8">
          <FormPanel
            loading={loading || mutationLoading}
            error={error || mutationError}
            onSubmit={async data => {
              const accreditationStatus =
                data.currentlyAccredited === 'YES'
                  ? AccreditationStatusEnumType.PendingAlindiaVerification
                  : data.qualifiesForAccreditation === 'QUALIFY'
                  ? AccreditationStatusEnumType.InitiateProcess
                  : AccreditationStatusEnumType.NotAccredited;
              try {
                await upsertAccreditationApplication({
                  variables: {
                    investmentEntityId,
                    accreditationStatus,
                    accreditationProofDocument: data.accreditationProofDocument
                      ? data.accreditationProofDocument
                      : undefined,
                    accreditationEligibilityCriteria: data.accreditationEligibilityCriteria || [],
                  },
                });
                await updateKycVerifyFlag({
                  variables: {
                    id: documentId,
                    verifyKyc: false,
                  },
                }).then(() => {
                  setDisplayMessage?.(true);
                  onClose();
                });
              } catch (err) {
                console.log(err);
              }
            }}
            submitButtonLabel="Submit"
          >
            <FormInput
              fullWidth
              type="radio-group"
              fieldName="currentlyAccredited"
              label="Do you currently have a valid Accreditation Certificate issued by a SEBI-approved KYC Registration Agency?"
              helperText="SEBI-approved KYC Registration Agencies are CDSL Ventures Limited (CVL) and NSDL Database Management Limited (NDML)"
              defaultValue={''}
              options={[
                { value: 'NO', label: 'No' },
                { value: 'YES', label: 'Yes' },
              ]}
              validators={{
                required: true,
              }}
              onChange={value => {
                setFormData(value);
              }}
            />
            <FormInput
              fieldName="accreditationProofDocument"
              label="Accreditation Proof Document *"
              helperText="Please provide an Accredited Investor (AI) Certificate"
              type="upload"
              defaultValue={''}
              validators={{
                required: true,
              }}
              conditionsToShow={{
                matches: [
                  {
                    field: 'currentlyAccredited',
                    condition: '===',
                    value: 'YES',
                  },
                ],
              }}
            />
            <FormInput
              fullWidth
              type="radio-group"
              fieldName="qualifiesForAccreditation"
              label="Do you qualify (individually or with a joint applicant) to obtain an accreditation certificate?"
              defaultValue={''}
              options={[
                {
                  value: 'DO_NOT_QUALIFY',
                  label: 'No, I do not qualify',
                },
                {
                  value: 'QUALIFY',
                  label: 'Yes, I qualify',
                },
              ]}
              validators={{
                required: true,
              }}
              conditionsToShow={{
                matches: [
                  {
                    field: 'currentlyAccredited',
                    condition: '===',
                    value: 'NO',
                  },
                ],
              }}
              onChange={value => {
                setFormData(value);
              }}
            />
            <div
              className={classNames(
                'my-1 col-span-full',
                formData.currentlyAccredited === 'NO' ? 'block' : 'hidden'
              )}
            >
              <JointAccreditationDetailsDialogText />
            </div>
            <FormInput
              fullWidth
              type="checkbox-group"
              fieldName="accreditationEligibilityCriteria"
              label="Which of the following criteria do you qualify for?"
              helperText="You can select multiple options if eligible"
              defaultValue={''}
              compact={true}
              options={[
                {
                  value: AccreditationEligibilityCriteriaEnumType.AnnualIncome,
                  label: 'Annual Income',
                  helperText: 'Annual income of at least INR 2 Crores',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.NetWorth,
                  label: 'Net Worth',
                  helperText: 'Net worth of at least INR 7.5 Crores',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.Combined,
                  label: 'Hybrid',
                  helperText:
                    'Annual income of at least INR 1 Crore and net worth of at least INR 5 Crores',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.JointApplicant,
                  label: 'Joint Application',
                  helperText:
                    'Combined with spouse, an Annual Income of at least INR 2 cr or Net Worth of at least INR 7.5 cr',
                },
              ]}
              validators={{
                required: true,
              }}
              conditionsToShow={{
                matches: [
                  {
                    field: 'currentlyAccredited',
                    condition: '===',
                    value: 'NO',
                  },
                  {
                    field: 'qualifiesForAccreditation',
                    condition: '===',
                    value: 'QUALIFY',
                  },
                ],
              }}
            />
            <div className="my-5" />
          </FormPanel>
        </div>
      </DialogPanel>
    </Dialog>
  );
};

export default AccreditationConfirmationDialogButton;
